import React from 'react';
import { Box, Typography, List, ListItem, Button, Collapse, FormControl, Select, MenuItem, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';

// Tipos de datos necesarios
interface Subespecialidad {
  uuid_especialidad: string;
  especialidad: string;
  descripcion: string[];
  esta_actualizado?: boolean | number;
}
interface EspecialidadesData {
  descripcion: string;
  esta_actualizado: boolean | number;
  subespecialidades: Subespecialidad[];
}
interface EspecialidadesResponse {
  [categoria: string]: EspecialidadesData;
}

interface ConfiguracionPanelProps {
  especialidades: EspecialidadesResponse | null;
  isLoading: boolean;
  expandedItems: { [key: string]: boolean };
  setExpandedItems: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  selectedSubespecialidades: { [key: string]: string };
  setSelectedSubespecialidades: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  isMobile: boolean;
  handleOpenModal: (index?: number) => void;
  handleOpenDeleteModal: (index: number) => void;
  handleOpenActualizarModal: () => void;
  closeConfig: () => void;
}

const ConfiguracionPanel: React.FC<ConfiguracionPanelProps> = ({
  especialidades,
  isLoading,
  expandedItems,
  setExpandedItems,
  selectedSubespecialidades,
  setSelectedSubespecialidades,
  isMobile,
  handleOpenModal,
  handleOpenDeleteModal,
  handleOpenActualizarModal,
  closeConfig
}) => (
  <Box sx={{ width: "100%", height: '100%', display: "flex", flexDirection: "column", overflow: "hidden", p: { xs: 0, md: 0 } }}>
    <Box sx={{ flexGrow: 1, overflow: "auto", p: 1, height: "calc(100dvh - 310px)", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "4px" }, "&::-webkit-scrollbar-thumb": { background: "#eb5f0a", borderRadius: "4px" }, "&::-webkit-scrollbar-thumb:hover": { background: "#d45500" } }}>
      {(especialidades && !isLoading) ? (
        <List>
          {Object.entries(especialidades).map(([categoria, data]) => (
            <Box key={categoria}>
              <Box sx={{ backgroundColor: "#f0f0f0", borderRadius: "8px", margin: "10px 0", overflow: "hidden" }}>
                <ListItem
                  onClick={() => {
                    setExpandedItems(prev => {
                      const newState = Object.keys(prev).reduce((acc, key) => ({
                        ...acc,
                        [key]: false
                      }), {});
                      return {
                        ...newState,
                        [categoria]: !prev[categoria]
                      };
                    });
                    if (expandedItems[categoria]) {
                      setSelectedSubespecialidades(prev => ({
                        ...prev,
                        [categoria]: ""
                      }));
                    }
                  }}
                  sx={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        backgroundColor: !!data.esta_actualizado ? "#4CAF50" : "#FFA726",
                        marginRight: "8px"
                      }}
                    />
                    <Typography variant="h6">{categoria}</Typography>
                  </Box>
                  <Typography>{expandedItems[categoria] ? "▼" : "▶"}</Typography>
                </ListItem>
                <Collapse in={expandedItems[categoria]}>
                  <Box sx={{ pl: 2, pr: 2, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "18px" }}>
                      *{data.descripcion}
                    </Typography>
                    <FormControl sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Select
                          displayEmpty
                          value={selectedSubespecialidades[categoria] || ""}
                          onChange={(e) => {
                            const newValue = e.target.value as string;
                            setSelectedSubespecialidades(prev => {
                              const clearedState = Object.keys(prev).reduce((acc, key) => ({
                                ...acc,
                                [key]: ""
                              }), {});
                              return {
                                ...clearedState,
                                [categoria]: newValue
                              };
                            });
                          }}
                          renderValue={() => {
                            return isMobile ?
                              <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                <LocalHospitalIcon sx={{ color: "text.secondary" }} />
                              </Box> :
                              <Typography color="text.secondary">Seleccionar subespecialidad</Typography>;
                          }}
                          sx={{ "& .MuiOutlinedInput-notchedOutline": { borderColor: "#eb5f0a" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#eb5f0a" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#eb5f0a" } }}
                        >
                          {data.subespecialidades.map((subespecialidad) => (
                            <MenuItem key={subespecialidad.uuid_especialidad} value={subespecialidad.uuid_especialidad}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "50%",
                                    backgroundColor: !!subespecialidad.esta_actualizado ? "#4CAF50" : "#FFA726",
                                    flexShrink: 0
                                  }}
                                />
                                {subespecialidad.especialidad}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                      <Box sx={{ backgroundColor: "#fafafa", width: "100%", height: { xs: "250px", md: "150px" }, borderRadius: "8px", position: "relative", display: "flex", flexDirection: "column" }}>
                        {selectedSubespecialidades[categoria] && (
                          <Box sx={{ p: 0.3, borderBottom: "1px solid #e0e0e0", display: "flex", justifyContent: "space-between", backgroundColor: "#fafafa", position: "sticky", top: 0, zIndex: 1, gap: 1, alignItems: "center", minWidth: 0, width: "100%" }}>
                            <Typography sx={{ fontWeight: "bold", overflow: "hidden", textOverflow: "ellipsis", display: "flex", alignItems: "center", pl: 1, flex: 1, minWidth: 0 }}>
                              {data.subespecialidades.find(sub =>
                                sub.uuid_especialidad === selectedSubespecialidades[categoria]
                              )?.especialidad}
                            </Typography>

                            <Typography sx={{ display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                              {data.subespecialidades.find(sub =>
                                sub.uuid_especialidad === selectedSubespecialidades[categoria]
                              )?.descripcion.length}/5
                            </Typography>

                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "30px", height: "30px", flexShrink: 0 }}>
                              <Typography
                                sx={{ cursor: "pointer", fontSize: "24px", lineHeight: 1, opacity: (() => { const subespecialidad = data.subespecialidades.find(sub => sub.uuid_especialidad === selectedSubespecialidades[categoria]); return (subespecialidad?.descripcion?.length || 0) >= 5 ? 0.5 : 1; })(), pointerEvents: (() => { const subespecialidad = data.subespecialidades.find(sub => sub.uuid_especialidad === selectedSubespecialidades[categoria]); return (subespecialidad?.descripcion?.length || 0) >= 5 ? "none" : "auto"; })() }}
                                onClick={() => handleOpenModal()}
                              >
                                +
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        <Box sx={{ overflow: "auto", flexGrow: 1 }}>
                          <List>
                            {selectedSubespecialidades[categoria] &&
                              data.subespecialidades
                                .find(sub => sub.uuid_especialidad === selectedSubespecialidades[categoria])
                                ?.descripcion.map((desc, index) => (
                                  <ListItem
                                    key={index}
                                    sx={{ pl: 1, pr: 1, pt: 0, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                  >
                                    <Typography sx={{ flex: 1 }}>
                                      {index + 1}. {desc}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <EditIcon
                                        sx={{ cursor: 'pointer', color: '#eb5f0a', '&:hover': { color: '#d45500' } }}
                                        onClick={() => handleOpenModal(index)}
                                      />
                                      <DeleteIcon
                                        sx={{ cursor: 'pointer', color: '#eb5f0a', '&:hover': { color: '#d45500' } }}
                                        onClick={() => handleOpenDeleteModal(index)}
                                      />
                                    </Box>
                                  </ListItem>
                                ))
                            }
                          </List>
                        </Box>
                      </Box>
                    </FormControl>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          ))}
        </List>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <CircularProgress sx={{ color: "#eb5f0a" }} />
        </Box>
      )}
    </Box>
    <Box sx={{ display: "flex", justifyContent: "end", alignItems: "center", p: 1, borderTop: 1, borderColor: "divider" }}>
      <Button
        sx={{
          background: "#eb5f0a",
          color: "white",
          '&:hover': { backgroundColor: "#d45500" },
          '&.Mui-disabled': {
            background: '#ffe2d1',
            color: '#bdbdbd',
            opacity: 1
          }
        }}
        onClick={handleOpenActualizarModal}
        disabled={isLoading || !especialidades}
      >
        ACTUALIZAR
      </Button>
    </Box>
  </Box>
);

export default ConfiguracionPanel; 